import { useCallback, useEffect, useState } from 'react';
import {
  getDownloadRecords as getDownloadRecordsApi,
  removeDownloadRecord as removeDownloadRecordApi,
  type InteractionTargetType,
} from '@/api/interaction';
import { useAuth } from '@/hooks/useAuth';

const DOWNLOADS_UPDATED_EVENT = 'wanxiang_downloads_updated';

export interface DownloadRecord {
  id: string;
  targetType: InteractionTargetType;
  targetId: string;
  toolId: string | null;
  toolName: string | null;
  toolCategory: string | null;
  version: string | null;
  size: string | null;
  downloadUrl: string | null;
  platform: string[];
  downloadedAt: string | null;
}

function emitDownloadsUpdated() {
  window.dispatchEvent(new CustomEvent(DOWNLOADS_UPDATED_EVENT));
}

export function useDownload(options?: { lazy?: boolean }) {
  const { user, isLoggedIn } = useAuth();
  const [records, setRecords] = useState<DownloadRecord[]>([]);
  const lazy = options?.lazy ?? false;

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setRecords([]);
      return;
    }
    try {
      const res = await getDownloadRecordsApi();
      setRecords(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Failed to fetch download records:', err);
      setRecords([]);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    if (!lazy) {
      void refresh();
    }
  }, [refresh, lazy]);

  useEffect(() => {
    const handleUpdated = () => {
      void refresh();
    };
    window.addEventListener(DOWNLOADS_UPDATED_EVENT, handleUpdated);
    return () => {
      window.removeEventListener(DOWNLOADS_UPDATED_EVENT, handleUpdated);
    };
  }, [refresh]);

  const removeRecord = useCallback(async (
    targetId: string,
    targetType: InteractionTargetType = 'tool',
  ) => {
    if (!isLoggedIn || !user?.id) {
      return { success: false, message: '请先登录' };
    }
    const numericId = Number(targetId);
    if (!Number.isInteger(numericId) || numericId <= 0) {
      return { success: false, message: '目标 ID 不合法' };
    }
    try {
      await removeDownloadRecordApi({ targetType, targetId: numericId });
      setRecords((prev) =>
        prev.filter((item) => !(item.targetType === targetType && item.targetId === String(numericId))),
      );
      emitDownloadsUpdated();
      return { success: true, message: '下载记录已移除' };
    } catch (err) {
      console.error('Failed to remove download record:', err);
      return { success: false, message: err instanceof Error ? err.message : '移除下载记录失败' };
    }
  }, [isLoggedIn, user?.id]);

  const isDownloaded = useCallback((toolId: string) => {
    const safeId = String(toolId);
    return records.some((item) => item.targetType === 'tool' && item.targetId === safeId);
  }, [records]);

  return { records, refresh, removeRecord, isDownloaded };
}
